import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { usePermissionsStore } from '@/stores/permissions.ts';

export function useAdminSidebar(rawItems) {
  const route = useRoute();
  const permissionsStore = usePermissionsStore();

  const isCollapsed = ref(false);
  const mobileMenu = ref(false);
  const expandedKeys = ref({});

  // null -> always visible; string => store.can(); string[] -> store.canAny()
  const hasPermission = (permission) => {
    if (permission === null) return true;
    if (Array.isArray(permission)) return permissionsStore.canAny(permission);
    return permissionsStore.can(permission);
  };

  const visibleItems = computed(() => {
    return rawItems.map(section => ({
        ...section,
        items: (section.items || []).filter(item => hasPermission(item.permission)),
      }))
      .filter(section => section.items.length > 0);
  });

  const isItemActive = (item, isActive, isExactActive) => {
    if (isActive || isExactActive) return true;
    if (item.relatedRoutes && Array.isArray(item.relatedRoutes)) {
      return item.relatedRoutes.includes(route.name);
    }
    return false;
  };

  const updateExpandedItems = () => {
    const currentRouteName = route.name;
    const next = { ...expandedKeys.value };

    visibleItems.value.forEach(section => {
      if (section.items && section.items.length > 0) {
        const hasActiveChild = section.items.some(item => {
          if (item.route?.name === currentRouteName) return true;
          if (item.relatedRoutes && Array.isArray(item.relatedRoutes)) {
            return item.relatedRoutes.includes(currentRouteName);
          }
          return false;
        });
        if (hasActiveChild) {
          next[section.key] = true;
        }
      }
    });

    expandedKeys.value = next;
  };

  const toggleSection = (key) => {
    expandedKeys.value = {
      ...expandedKeys.value,
      [key]: !expandedKeys.value[key],
    };
  };
  onMounted(() => {
    updateExpandedItems();
  });

  watch(route, () => {
    updateExpandedItems();
    mobileMenu.value = false;
  });

  watch(mobileMenu, (val) => {
    if (val) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  });

  return {
    isCollapsed,
    mobileMenu,
    expandedKeys,
    visibleItems,
    isItemActive,
    toggleSection,
  };
}
